import type { NavConfig } from '@pressreader/navigation-types';

import { getReaderArticleId } from './reader.params';

/**
 * An entry that designates an article as the reader's, under one of the two spellings of the parameter:
 * `aid`, or the legacy `popupArticleId`. A union, so that a config carrying neither is never one of these
 * and the id is read through `getReaderArticleId` rather than off a field that may be absent.
 */
export type NavConfigWithArticlePopupId = NavConfig & ({ aid: string } | { popupArticleId: string });

/**
 * Whether an article is designated as the reader's on this entry, under either spelling of the
 * parameter — `aid` or the legacy `popupArticleId`.
 */
export function isNavConfigWithArticlePopupId(config: NavConfig): config is NavConfigWithArticlePopupId {
    return getReaderArticleId(config) !== null;
}

/**
 * Whether the entry carries the legacy popup marker itself — the spelling legacy-owned contexts and
 * the shared-article landing rewrite still write. Code that owns that legacy path reads this;
 * everything else asks `isNavConfigWithArticlePopupId`.
 */
export function isLegacyArticlePopupNavConfig(config: NavConfig): config is NavConfig & { popupArticleId: string } {
    const { popupArticleId } = config as NavConfig & { popupArticleId?: string };

    return typeof popupArticleId === 'string' && popupArticleId !== '';
}
